import { PageTitle, H2Heading } from "../../../components/Headers";
import { PageBody } from "../../../components/PageBody";
import { Helmet } from "react-helmet-async";
import { ArticleParagraph } from "../../../components/Paragraphs";
import { GridDiv, GridDivContainer } from "../../../components/GridDiv";
import { ProjectLink } from "../../../components/ProjectLink";
import { getProjectsForSection } from "../../../config/projects";

// Get golf projects from centralized config
const golfProjects = getProjectsForSection('/sports-models/golf');


export const GolfHome = () => {
    return (
        <PageBody>
            <Helmet>
                <title>Modelling Golf | VizBadger</title>
                <meta
                    name="description"
                    content="Distilling the game of golf into programmatic form using Monte Carlo simulation at multiple levels of abstraction. Round-level, tournament-level, and shot-level simulation."
                ></meta>
                <meta name="keywords" content="Sports models, sports prediction, golf modelling, golf simulation, golf stats, predictive analytics, sports analytics, monte carlo simulation, strokes gained"></meta>
            </Helmet>

            <PageTitle title="Modelling Golf" />

            <ArticleParagraph
                lines={[
                    "This section explores what happens when you try to distil the game of golf into programmatic form. Golf is a good candidate for simulation: it has a standardised skill measurement system (the handicap index), well-documented performance statistics, and outcomes that emerge from the interaction of player ability, course characteristics, and situational factors.",
                    "The simulations here operate at three levels of abstraction. At the round level, a handicap-based scoring distribution models the probability of each score on each hole, producing realistic round totals without simulating individual shots. At the tournament level, golfer profiles introduce skill shape, course fit, weather resilience, and pressure handling across multiple rounds with cuts. At the shot level, individual holes are rendered in 2D with terrain zones, and each shot is sampled from a spatial distribution influenced by club selection, strategy, and lie conditions.",
                ]}
            />

            <H2Heading>The golfer data model</H2Heading>
            <ArticleParagraph
                lines={[
                    "Underpinning the tournament and shot-level simulations is a data model that represents a golfer as more than a single number. The handicap index serves as the primary axis of ability, but skill modifiers — inspired by strokes gained categories — describe the shape of that ability: whether a golfer is strong off the tee but weak on the greens, or a brilliant putter who struggles with approach play. Consistency, weather resilience, and pressure handling add further dimensions that interact with course and tournament characteristics to produce differentiated outcomes.",
                ]}
            />

            <GridDivContainer>
                {golfProjects.map((project) => (
                    <GridDiv
                        key={project.to}
                        widthClasses="w-full sm:w-1/2"
                    >
                        <ProjectLink
                            to={project.to}
                            title={project.title}
                            icon={project.icon}
                            colorClass={project.colorClass}
                            isCustomIcon={project.isCustomIcon}
                        />
                    </GridDiv>
                ))}
            </GridDivContainer>
        </PageBody>
    );
};
